// src/features/tuition/components/students/StudentDetail.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Tab,
  Tabs,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PersonOutline as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  EventNote as EventNoteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  fetchStudentNotes, 
  fetchSessionsByStudent, 
  fetchPaymentsByStudent,
  addStudentNote
} from '../../services/studentsService';
import SessionForm from '../scheduling/SessionForm';

const StudentDetail = ({ student, onBack, onEdit, onMenuOpen }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [notes, setNotes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [isSessionFormOpen, setIsSessionFormOpen] = useState(false);
  
  useEffect(() => {
    if (student) {
      loadStudentData();
    }
  }, [student, activeTab]);
  
  const loadStudentData = async () => {
    if (!student) return;
    
    setLoading(true);
    try {
      // Load data based on active tab
      if (activeTab === 'notes' || activeTab === 'overview') {
        const notesData = await fetchStudentNotes(student.id);
        setNotes(notesData);
      }
      
      if (activeTab === 'sessions' || activeTab === 'overview') {
        const sessionsData = await fetchSessionsByStudent(student.id);
        setSessions(sessionsData);
      }
      
      if (activeTab === 'payments' || activeTab === 'overview') {
        const paymentsData = await fetchPaymentsByStudent(student.id);
        setPayments(paymentsData);
      }
    } catch (error) {
      console.error("Error loading student data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    setAddingNote(true);
    try {
      const newNote = await addStudentNote(student.id, noteText);
      setNotes([newNote, ...notes]);
      setNoteText('');
    } catch (error) {
      console.error("Error adding note:", error);
    } finally {
      setAddingNote(false);
    }
  };
  
  if (!student) {
    return null;
  }

  const handleAddSession = () => {
    setIsSessionFormOpen(true);
  };
  
  const handleSessionFormClose = () => {
    setIsSessionFormOpen(false);
  };
  
  const handleSessionFormSubmit = async (sessionData) => {
    try {
      // Session form handles the submission logic
      setIsSessionFormOpen(false);
      
      // Reload sessions data
      if (activeTab === 'sessions' || activeTab === 'overview') {
        const sessionsData = await fetchSessionsByStudent(student.id);
        setSessions(sessionsData);
      }
    } catch (error) {
      console.error("Error adding session:", error);
    }
  };
  
  const getStatusColor = (status) => {
    return status === 'active' ? '#4CAF50' : '#757575';
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            Student Details
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => onEdit(student)}
            sx={{
              mr: 1,
              borderColor: THEME_COLORS.primary,
              color: THEME_COLORS.primary,
              '&:hover': {
                borderColor: '#1e1c4e',
                bgcolor: 'rgba(39, 35, 97, 0.04)'
              }
            }}
          >
            Edit
          </Button>
          <IconButton onClick={(e) => onMenuOpen(e, student.id)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                p: 3, 
                bgcolor: 'rgba(39, 35, 97, 0.04)',
                borderRadius: 2,
                mb: 2
              }}
            >
              <Avatar
                sx={{
                  bgcolor: THEME_COLORS.primary,
                  width: 80,
                  height: 80,
                  fontSize: 32,
                  mb: 2
                }}
              >
                {student.firstName?.charAt(0) || ''}
              </Avatar>
              <Typography variant="h5" fontWeight="bold" align="center">
                {student.firstName} {student.lastName}
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Grade {student.grade}
              </Typography>
              <Chip 
                label={student.status === 'active' ? 'Active' : 'Inactive'} 
                sx={{ 
                  mt: 1,
                  bgcolor: `${getStatusColor(student.status)}20`,
                  color: getStatusColor(student.status),
                  fontWeight: 'medium'
                }} 
              />
              
              <Box sx={{ width: '100%', mt: 3 }}>
                <List disablePadding>
                  {student.school && (
                    <ListItem disablePadding sx={{ mb: 2 }}>
                      <SchoolIcon 
                        sx={{ 
                          mr: 2, 
                          color: 'text.secondary',
                          fontSize: 20 
                        }} 
                      />
                      <ListItemText 
                        primary="School"
                        secondary={student.school}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  
                  {student.email && (
                    <ListItem disablePadding sx={{ mb: 2 }}>
                      <EmailIcon 
                        sx={{ 
                          mr: 2, 
                          color: 'text.secondary',
                          fontSize: 20 
                        }} 
                      />
                      <ListItemText 
                        primary="Email"
                        secondary={student.email}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  
                  {student.contactNumber && (
                    <ListItem disablePadding sx={{ mb: 2 }}>
                      <PhoneIcon 
                        sx={{ 
                          mr: 2, 
                          color: 'text.secondary',
                          fontSize: 20 
                        }} 
                      />
                      <ListItemText 
                        primary="Contact"
                        secondary={student.contactNumber}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  
                  {student.startDate && (
                    <ListItem disablePadding>
                      <EventNoteIcon 
                        sx={{ 
                          mr: 2, 
                          color: 'text.secondary',
                          fontSize: 20 
                        }} 
                      />
                      <ListItemText 
                        primary="Start Date"
                        secondary={new Date(student.startDate).toLocaleDateString()}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>
            
            <Card sx={{ mb: 2 }}>
              <CardHeader 
                title="Subjects" 
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} 
              />
              <Divider />
              <CardContent>
                {student.subjects && student.subjects.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {student.subjects.map((subject, index) => (
                      <Chip 
                        key={index} 
                        label={subject} 
                        sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }} 
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No subjects assigned
                  </Typography>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader 
                title="Parent/Guardian" 
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} 
              />
              <Divider />
              <CardContent>
                {(student.parentName || student.parentEmail || student.parentContact) ? (
                  <List disablePadding>
                    {student.parentName && (
                      <ListItem disablePadding sx={{ mb: 2 }}>
                        <PersonIcon 
                          sx={{ 
                            mr: 2, 
                            color: 'text.secondary',
                            fontSize: 20 
                          }} 
                        />
                        <ListItemText 
                          primary="Name"
                          secondary={student.parentName}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                    
                    {student.parentEmail && (
                      <ListItem disablePadding sx={{ mb: 2 }}>
                        <EmailIcon 
                          sx={{ 
                            mr: 2, 
                            color: 'text.secondary',
                            fontSize: 20 
                          }} 
                        />
                        <ListItemText 
                          primary="Email"
                          secondary={student.parentEmail}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                    
                    {student.parentContact && (
                      <ListItem disablePadding>
                        <PhoneIcon 
                          sx={{ 
                            mr: 2, 
                            color: 'text.secondary',
                            fontSize: 20 
                          }} 
                        />
                        <ListItemText 
                          primary="Contact"
                          secondary={student.parentContact}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    No parent/guardian information
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Overview" 
                value="overview" 
                sx={{ 
                  textTransform: 'none',
                  fontWeight: activeTab === 'overview' ? 'bold' : 'normal'
                }} 
              />
              <Tab 
                label="Notes" 
                value="notes" 
                sx={{ 
                  textTransform: 'none',
                  fontWeight: activeTab === 'notes' ? 'bold' : 'normal'
                }} 
              />
              <Tab 
                label="Sessions" 
                value="sessions" 
                sx={{ 
                  textTransform: 'none',
                  fontWeight: activeTab === 'sessions' ? 'bold' : 'normal'
                }} 
              />
              <Tab 
                label="Payments" 
                value="payments" 
                sx={{ 
                  textTransform: 'none',
                  fontWeight: activeTab === 'payments' ? 'bold' : 'normal'
                }} 
              />
            </Tabs>
            
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress sx={{ color: THEME_COLORS.primary }} />
              </Box>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <Box>
                    {student.goals && (
                      <Card sx={{ mb: 3 }}>
                        <CardHeader 
                          title="Learning Goals" 
                          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} 
                        />
                        <Divider />
                        <CardContent>
                          <Typography>{student.goals}</Typography>
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Recent Notes */}
                    <Card sx={{ mb: 3 }}>
                      <CardHeader 
                        title="Recent Notes" 
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                        action={
                          <Button
                            size="small"
                            onClick={() => setActiveTab('notes')}
                            sx={{ color: THEME_COLORS.primary }}
                          >
                            View All
                          </Button>
                        }
                      />
                      <Divider />
                      <CardContent>
                        {notes.length > 0 ? (
                          <List disablePadding>
                            {notes.slice(0, 3).map((note) => (
                              <ListItem key={note.id} sx={{ px: 0, py: 1 }}>
                                <ListItemText
                                  primary={note.text}
                                  secondary={new Date(note.createdAt).toLocaleString()}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography color="text.secondary">
                            No notes yet
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Upcoming Sessions */}
                    <Card sx={{ mb: 3 }}>
                      <CardHeader 
                        title="Upcoming Sessions" 
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                        action={
                          <Button
                            size="small"
                            onClick={() => setActiveTab('sessions')}
                            sx={{ color: THEME_COLORS.primary }}
                          >
                            View All
                          </Button>
                        }
                      />
                      <Divider />
                      <CardContent>
                        {sessions.filter(s => s.status === 'scheduled').length > 0 ? (
                          <List disablePadding>
                            {sessions
                              .filter(s => s.status === 'scheduled')
                              .slice(0, 3)
                              .map((session) => (
                                <ListItem key={session.id} sx={{ px: 0, py: 1 }}>
                                  <ListItemText
                                    primary={`${session.subject} - ${session.date}`}
                                    secondary={`${session.startTime} - ${session.endTime}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                </ListItem>
                              ))
                            }
                          </List>
                        ) : (
                          <Typography color="text.secondary">
                            No upcoming sessions
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Recent Payments */}
                    <Card>
                      <CardHeader 
                        title="Recent Payments" 
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                        action={
                          <Button
                            size="small"
                            onClick={() => setActiveTab('payments')}
                            sx={{ color: THEME_COLORS.primary }}
                          >
                            View All
                          </Button>
                        }
                      />
                      <Divider />
                      <CardContent>
                        {payments.length > 0 ? (
                          <List disablePadding>
                            {payments.slice(0, 3).map((payment) => (
                              <ListItem key={payment.id} sx={{ px: 0, py: 1 }}>
                                <ListItemText
                                  primary={formatCurrency(payment.amount)}
                                  secondary={`${payment.description} - ${new Date(payment.date).toLocaleDateString()}`}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography color="text.secondary">
                            No payment records
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {/* Notes Tab */}
                {activeTab === 'notes' && (
                  <Box>
                    <Paper sx={{ p: 2, mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Add Note
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Type a note about this student..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Box display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          onClick={handleAddNote}
                          disabled={!noteText.trim() || addingNote}
                          startIcon={addingNote && <CircularProgress size={20} color="inherit" />}
                          sx={{
                            bgcolor: THEME_COLORS.primary,
                            '&:hover': { bgcolor: '#1e1c4e' }
                          }}
                        >
                          {addingNote ? 'Adding...' : 'Add Note'}
                        </Button>
                      </Box>
                    </Paper>
                    
                    <Card>
                      <CardHeader 
                        title="All Notes" 
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} 
                      />
                      <Divider />
                      <CardContent>
                        {notes.length > 0 ? (
                          <List>
                            {notes.map((note) => (
                              <ListItem 
                                key={note.id} 
                                sx={{ 
                                  px: 2, 
                                  py: 2,
                                  borderLeft: '3px solid',
                                  borderColor: THEME_COLORS.primary,
                                  bgcolor: 'rgba(39, 35, 97, 0.04)',
                                  borderRadius: 1,
                                  mb: 2
                                }}
                              >
                                <ListItemText
                                  primary={note.text}
                                  secondary={new Date(note.createdAt).toLocaleString()}
                                  primaryTypographyProps={{ paragraph: true }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Box textAlign="center" py={4}>
                            <Typography color="text.secondary" paragraph>
                              No notes for this student yet
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => document.getElementById('note-input').focus()}
                              sx={{
                                borderColor: THEME_COLORS.primary,
                                color: THEME_COLORS.primary,
                                '&:hover': {
                                  borderColor: '#1e1c4e',
                                  bgcolor: 'rgba(39, 35, 97, 0.04)'
                                }
                              }}
                            >
                              Add First Note
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {/* Sessions Tab */}
                {activeTab === 'sessions' && (
                  <Card>
                    <CardHeader 
                      title="Tuition Sessions" 
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                      action={
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                            sx={{
                              bgcolor: THEME_COLORS.primary,
                              '&:hover': { bgcolor: '#1e1c4e' }
                            }}
                            onClick={handleAddSession}
                          >
                            Add Session
                          </Button>
                      }
                    />
                    <Divider />
                    <CardContent>
                      {sessions.length > 0 ? (
                        <List>
                          {sessions.map((session) => (
                            <ListItem 
                              key={session.id} 
                              sx={{ 
                                px: 2, 
                                py: 2,
                                borderLeft: '3px solid',
                                borderColor: session.status === 'completed' ? '#4CAF50' : 
                                            session.status === 'scheduled' ? THEME_COLORS.primary : '#757575',
                                bgcolor: 'rgba(39, 35, 97, 0.04)',
                                borderRadius: 1,
                                mb: 2
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle2">
                                      {session.subject}
                                    </Typography>
                                    <Chip 
                                      label={session.status} 
                                      size="small"
                                      sx={{ 
                                        textTransform: 'capitalize',
                                        bgcolor: session.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' : 
                                                  session.status === 'scheduled' ? 'rgba(33, 150, 243, 0.1)' : 
                                                  'rgba(158, 158, 158, 0.1)',
                                        color: session.status === 'completed' ? '#4CAF50' : 
                                               session.status === 'scheduled' ? '#2196F3' : 
                                               '#757575',
                                      }} 
                                    />
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Typography variant="body2">
                                      {new Date(session.date).toLocaleDateString()} | {session.startTime} - {session.endTime}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box textAlign="center" py={4}>
                          <Typography color="text.secondary" paragraph>
                            No sessions scheduled for this student
                          </Typography>
                          <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddSession}
                          sx={{
                            bgcolor: THEME_COLORS.primary,
                            '&:hover': { bgcolor: '#1e1c4e' }
                          }}
                        >
                          Schedule Session
                        </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Payments Tab */}
                {activeTab === 'payments' && (
                  <Card>
                    <CardHeader 
                      title="Payment History" 
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                      action={
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          sx={{
                            bgcolor: THEME_COLORS.primary,
                            '&:hover': { bgcolor: '#1e1c4e' }
                          }}
                        >
                          Record Payment
                        </Button>
                      }
                    />
                    <Divider />
                    <CardContent>
                      {payments.length > 0 ? (
                        <List>
                          {payments.map((payment) => (
                            <ListItem 
                              key={payment.id} 
                              sx={{ 
                                px: 2, 
                                py: 2,
                                borderLeft: '3px solid',
                                borderColor: '#4CAF50',
                                bgcolor: 'rgba(76, 175, 80, 0.04)',
                                borderRadius: 1,
                                mb: 2
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle2">
                                      {payment.description}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {formatCurrency(payment.amount)}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(payment.date).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                      {payment.method}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box textAlign="center" py={4}>
                          <Typography color="text.secondary" paragraph>
                            No payment records for this student
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{
                              bgcolor: THEME_COLORS.primary,
                              '&:hover': { bgcolor: '#1e1c4e' }
                            }}
                          >
                            Record First Payment
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
      {/* Session Form Dialog */}
      <SessionForm
        open={isSessionFormOpen}
        onClose={handleSessionFormClose}
        onSubmit={handleSessionFormSubmit}
        preselectedStudent={student}
      />
    </Box>
  );
};

export default StudentDetail;